import { ClockIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { Overview } from 'components/Overview'
import { DateTime } from 'luxon'
import { AddExpirationDateTypeModal } from 'pages/Admin/AdminTools/ExpirationDateType/AddExpirationDateTypeModal'
import { IExpirationDateConfigItem } from 'pages/Admin/AdminTools/ExpirationDateType/type'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  createExpirationDates,
  deleteExpirationDate,
  getExpirationDates,
  resetExpirationDate,
  updateExpirationDates,
} from 'services'
import { Button, Checkbox } from 'stories/components'
import { confirm, formatDate, isBeforeOrEqualToday, validDateMMDDYYYY } from 'utils'
import { getInvisibleFields } from 'utils/loan'
import { setLoanNumber } from 'utils/setLoanNumber'

import { ExpirationDatesPropUpdate } from './ExpirationDatesPropUpdate'
import { ExpirationDatesStatus } from './ExpirationDatesStatus'
import { ExpirationDatesUpdate } from './ExpirationDatesUpdate'
import { HistoryModal } from './HistoryModal'
import { ExpirationDatesItem } from './types'

const IDkeys = ['driverLicense', 'passport', 'stateId', 'eadCard', 'alienCard']

const additionalKeyPrefix = 'additional_'

export default function ExpirationDates({ child = false }) {
  const [loading, setLoading] = useState(false)
  const [isOpenType, setIsOpenType] = useState<string>('')
  const [historyType, setHistoryType] = useState('')
  const [loan] = useSelector((state: any) => [state.loanDetail])
  const { includeCoborrower } = loan

  const [data, setData] = useState<Record<string, ExpirationDatesItem>>({})
  const [closingDate, setClosingDate] = useState('')
  const [config, setConfig] = useState<Record<string, IExpirationDateConfigItem>>()
  const [order, setOrder] = useState<string[]>([])
  const [selectedType, setSelectedType] = useState('')
  const [showAddModal, setShowAddModal] = useState(false)

  useEffect(() => {
    setLoanNumber()
    refetch()
  }, [])

  const sortedKeys = useMemo(() => {
    const isIDKey = (key: string) => {
      let rtl = false
      IDkeys.map((idKey: string) => {
        if (key.startsWith(idKey)) rtl = true
      })
      return rtl
    }

    const getOrder = (key: string) => {
      let idxA = order.indexOf(key)
      if (idxA == -1) idxA = order.indexOf(key.substring(0, key.length - 1))
      return idxA
    }

    const originKeys = Object.keys(data).filter((key) => !key.startsWith(additionalKeyPrefix))
    let rtl = originKeys.filter((key) => !data[key].isCoBorrower && !isIDKey(key))
    rtl.push(...originKeys.filter((key) => data[key].isCoBorrower && !isIDKey(key)))

    rtl = rtl.sort((a, b) => getOrder(a) - getOrder(b))

    rtl.push(...originKeys.filter((key) => !data[key].isCoBorrower && isIDKey(key)))
    rtl.push(...originKeys.filter((key) => data[key].isCoBorrower && isIDKey(key)))

    return rtl
  }, [data])

  const additionalKeys = useMemo(() => Object.keys(data).filter((key) => key.startsWith(additionalKeyPrefix)), [data])

  const renderIDRestriction = useMemo(() => {
    let exist = false
    IDkeys.map((key) => {
      if (!data[key]?.isCoBorrower && validDateMMDDYYYY(data[key]?.expirationDate)) exist = true
    })
    if (!exist) {
      return (
        <tr key="IDKeys">
          <td colSpan={6} className="bg-red-100 px-4 py-3 text-red-700 font-variation-settings-600">
            Borrower 1 ID (At least 1 of the following EXPIRATION DATE is Required!)
          </td>
        </tr>
      )
    }
    return (
      <tr key="IDKeys">
        <td colSpan={6} className="bg-gray-200 px-4 py-3 text-gray-700 font-variation-settings-600">
          Borrower 1 ID
        </td>
      </tr>
    )
  }, [data])

  const invisibleKeys = useMemo(() => {
    if (!config) return []

    return getInvisibleFields('ExpirationDates' as any, null, {
      additionalFieldProps: {
        ExpirationDates: config as any,
      },
    })
  }, [config])

  const renderIDRestrictionCoBorrower = (index: number) => {
    let exist = false
    IDkeys.map((key) => {
      key = `${key}CoBorrower${index || ''}`
      if (data[key]?.isCoBorrower && validDateMMDDYYYY(data[key]?.expirationDate)) exist = true
    })
    if (index == 0) index = 1

    if (!exist) {
      return (
        <tr key="IDKeys-Coborrower">
          <td colSpan={6} className="bg-red-100 px-4 py-3 text-red-700 font-variation-settings-600">
            Borrower {index + 1} ID (At least 1 of the following EXPIRATION DATE is Required!)
          </td>
        </tr>
      )
    }
    return (
      <tr key="IDKeys-Coborrower">
        <td colSpan={6} className="bg-gray-200 px-4 py-3 text-gray-700 font-variation-settings-600">
          Borrower {index + 1} ID
        </td>
      </tr>
    )
  }

  const refetch = async () => {
    setLoading(true)
    const { data, closingDate, config } = await getExpirationDates()
    setData(data)
    setClosingDate(closingDate)
    setOrder(config.order)
    setConfig(config.data)

    setLoading(false)
  }

  const onEditDate = (type: string) => {
    setIsOpenType('date')
    setSelectedType(type)
  }

  const onEditDays = (type: string) => {
    setIsOpenType('day')
    setSelectedType(type)
  }

  const onEditExpDate = (type: string) => {
    setIsOpenType('expirationDate')
    setSelectedType(type)
  }

  const onEditProps = (type: string) => {
    setIsOpenType(type.startsWith(additionalKeyPrefix) ? 'additionalEdit' : 'prop')
    setSelectedType(type)
  }

  const onCloseModal = () => {
    setIsOpenType('')
    setSelectedType('')
  }

  const onUpdateDate = (value: string) => {
    onChange(isOpenType, formatDate(value), selectedType)
    onCloseModal()
  }

  const onUpdateDays = (value: number) => {
    onChange('days', value, selectedType)
    onCloseModal()
  }

  const onUpdateProp = async (props: Record<string, any>) => {
    setLoading(true)
    const type = selectedType
    await updateExpirationDates(type, props)
    setLoading(false)

    const newData = cloneDeep(data)
    ;(newData[type] as any) = {
      ...newData[type],
      ...props,
    }

    setData(newData)
    onCloseModal()
  }

  const onUpdateReason = async (reason: string, status: boolean, type: string) => {
    if (type === undefined) type = selectedType
    setLoading(true)
    await updateExpirationDates(type, { reason, status })
    setLoading(false)
    const newData = cloneDeep(data)
    newData[type].reason = reason
    newData[type].status = status
    setData(newData)
    onCloseModal()
  }

  const onChange = async (key: string, value: string | number, type: string) => {
    setLoading(true)
    await updateExpirationDates(type, { [key]: value })
    setLoading(false)

    const newData = cloneDeep(data)
    ;(newData[type] as any)[key] = value

    if (!newData[type].type && newData[type].days !== null && newData[type].date && ['date', 'days'].includes(key)) {
      const dt = DateTime.fromJSDate(new Date(newData[type].date)).plus({ days: newData[type].days })
      newData[type].expirationDate = dt.toFormat('MM/dd/yyyy')
    }

    setData(newData)
  }

  const onUpdateStatus = async (type: string) => {
    const newData = cloneDeep(data)
    const { status } = newData[type]
    if (!status) {
      setIsOpenType('status')
      setSelectedType(type)
    } else {
      setLoading(true)
      await onUpdateReason('', false, type)
      setLoading(false)
    }
  }

  const historyView = async (type: string) => {
    setHistoryType(type)
  }

  const onReset = async (type: string, key: string) => {
    const item = data[type]
    const title = `${item.title} ${key === 'date' ? 'Date' : 'Expiration Date'}`
    const result = await confirm(`Are you sure to reset ${title}?`, {
      title,
    })
    if (!result) return

    setLoading(true)
    let keys = [key]
    if (key === 'date' && item.days !== null) keys.push('expirationDate')
    await resetExpirationDate(type, keys)
    setLoading(false)

    const newData: any = cloneDeep(data)
    keys.map((key) => {
      newData[type][key] = ''
    })
    setData(newData)
  }

  const onAddAdditional = async (result: Record<string, any> | null) => {
    setShowAddModal(false)
    if (!result) return

    const newKey = `${additionalKeyPrefix}${Date.now()}`
    const newItem: ExpirationDatesItem = {
      title: result.title,
      date: '',
      days: result.type ? 0 : Number(result.days),
      expirationDate: '',
      type: result.type ? 1 : 0,
      seasonType: result.seasonType ? 1 : 0,
      isCoBorrower: 0,
      calculateBasedOn: Number(result.calculateBasedOn) || 1,
      requirePass: false,
      status: false,
      reason: '',
      history: [],
      isCustom: true,
    }

    setLoading(true)
    await createExpirationDates(newKey, newItem)
    setLoading(false)

    const newData = cloneDeep(data)
    newData[newKey] = newItem
    setData(newData)
  }

  const onDeleteAdditional = async (type: string) => {
    const result = await confirm(`Are you sure to delete "${data[type]?.title}"?`)
    if (!result) return

    setLoading(true)
    await deleteExpirationDate(type)
    setLoading(false)

    const newData = cloneDeep(data)
    delete newData[type]
    setData(newData)
  }

  const onUpdateAdditional = async (result: Record<string, any> | null) => {
    if (!result) {
      onCloseModal()
      return
    }

    const type = selectedType
    const updates = {
      title: result.title,
      days: result.type ? 0 : Number(result.days),
      type: result.type ? 1 : 0,
      seasonType: result.seasonType ? 1 : 0,
      calculateBasedOn: Number(result.calculateBasedOn) || 1,
    }

    setLoading(true)
    await updateExpirationDates(type, updates)
    setLoading(false)

    const newData = cloneDeep(data)
    newData[type] = { ...newData[type], ...updates }
    setData(newData)
    onCloseModal()
  }

  const renderDatePresent = (value: string) => {
    const ok = validDateMMDDYYYY(value)
    return (
      <span
        className={`text-[12px] px-2 py-0.5 text-center rounded ${ok ? 'bg-green-100' : 'bg-red-100'} w-[40px] shrink-0`}
      >
        {ok ? 'Yes' : 'No'}
      </span>
    )
  }

  const renderRow = (type: string, index: number) => {
    if (invisibleKeys.includes(type)) return null

    const item = data[type]

    return (
      <tr key={type} className={`${index % 2 ? 'bg-gray-50' : ''}`}>
        <td className="px-4 py-3">{item.title}</td>
        <td className={`px-4 py-3 ${item.startDatePass === false && 'border-[3px] border-red-200'}`}>
          {!item.type ? (
            <div className="flex flex-row items-center gap-2">
              {renderDatePresent(item.date)}
              <p>{item.date}</p>
              <span
                className="text-shade-blue cursor-pointer hover-shadow1 p-1 rounded"
                onClick={() => onEditDate(type)}
              >
                <PencilSquareIcon className="w-4 h-4"></PencilSquareIcon>
              </span>
              {item.date && (
                <span
                  className="text-red-700 cursor-pointer hover-shadow1 p-1 rounded"
                  onClick={() => onReset(type, 'date')}
                >
                  <TrashIcon className="w-4 h-4" />
                </span>
              )}
            </div>
          ) : (
            '-'
          )}
        </td>
        <td className="px-4 py-3">
          {!item.type && item.days !== null ? (
            <div className="flex flex-row items-center">
              <p className="w-[24px]">{item.days}</p>
              <span
                className="text-shade-blue cursor-pointer hover-shadow1 p-1 rounded ml-3"
                onClick={() => onEditDays(type)}
              >
                <PencilSquareIcon className="w-4 h-4"></PencilSquareIcon>
              </span>
            </div>
          ) : (
            '-'
          )}
        </td>
        <td className={`px-4 py-3 ${item.requirePass === false && 'border-[3px] border-red-200'}`}>
          {!item.type && item.days !== null ? (
            <div className="flex flex-row items-center gap-2">
              {renderDatePresent(item.expirationDate)}
              <p>{item.expirationDate}</p>
            </div>
          ) : (
            <div className="flex flex-row items-center gap-2">
              {renderDatePresent(item.expirationDate)}
              <p>{item.expirationDate}</p>
              <span
                className="text-shade-blue cursor-pointer hover-shadow1 p-1 rounded"
                onClick={() => onEditExpDate(type)}
              >
                <PencilSquareIcon className="w-4 h-4" />
              </span>
              {item.expirationDate && (
                <span
                  className="text-red-700 cursor-pointer hover-shadow1 p-1 rounded"
                  onClick={() => onReset(type, 'expirationDate')}
                >
                  <TrashIcon className="w-4 h-4" />
                </span>
              )}
            </div>
          )}
        </td>
        <td className="px-4 py-3">{renderStatus(item, type)}</td>
        <td className="px-4 py-3">
          <Checkbox
            title="Override"
            value={item.status}
            checked={item.status}
            id={type}
            onClick={() => onUpdateStatus(type)}
            className="w-fit"
          />
        </td>
        <td className="py-3">
          <div className="flex gap-2 items-center">
            <span
              className="text-shade-blue cursor-pointer hover-shadow1 p-1 rounded"
              onClick={() => onEditProps(type)}
            >
              <PencilSquareIcon className="w-4 h-4" />
            </span>
            <span
              className="text-shade-blue cursor-pointer hover-shadow1 p-1 rounded"
              onClick={() => historyView(type)}
            >
              <ClockIcon className="w-4 h-4" />
            </span>
            {type.startsWith(additionalKeyPrefix) && (
              <span
                className="text-red-700 cursor-pointer hover-shadow1 p-1 rounded"
                onClick={() => onDeleteAdditional(type)}
              >
                <TrashIcon className="w-4 h-4" />
              </span>
            )}
          </div>
        </td>
      </tr>
    )
  }

  const renderTable = (seasonType: number = 0) => {
    return (
      <div className="overflow-auto shadow-md sm:rounded-lg">
        <table className="w-full text-sm text-left text-gray-900 dark:text-gray-400 pl-6">
          <thead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 border-b border-slate-200 dark:bg-gray-700 dark:text-gray-400">
            <tr>
              <th scope="col" className="px-4 py-3">
                Type
              </th>
              <th scope="col" className="px-4 py-3 min-w-[200px]">
                Date
              </th>
              <th scope="col" className="px-4 py-3">
                Days - Prior to Note Date
              </th>
              <th scope="col" className="px-4 py-3">
                Expiration Date
              </th>
              <th scope="col" className="px-4 py-3">
                Status
              </th>
              <th scope="col" className="px-4 py-3">
                Override Validation
              </th>
              <th scope="col" className="pr-4 py-3">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="text-[15px] text-gray-900">
            {additionalKeys.map((key, index) => {
              const item = data[key]
              if (!item || item.seasonType != seasonType) return null
              return renderRow(key, index)
            })}
            {sortedKeys.map((type, index) => {
              const item = data[type]
              if (item.seasonType != seasonType) return null
              if (!includeCoborrower && item.isCoBorrower) return null

              return [
                (type === 'driverLicense' && renderIDRestriction) ||
                  (type.includes('driverLicenseCoBorrower') &&
                    includeCoborrower &&
                    renderIDRestrictionCoBorrower(Number(type.replace('driverLicenseCoBorrower', '') || 0))),
                renderRow(type, index),
              ].filter((v) => !!v)
            })}
          </tbody>
        </table>
      </div>
    )
  }

  const renderStatus = (item: ExpirationDatesItem, itemType: string) => {
    const { status, reason, date, type, expirationDate, seasonType, calculateBasedOn: _calculateBasedOn } = item
    if (status) {
      return (
        <div className="text-[14px] w-[120px]">
          <p className={`px-2 py-1 text-center bg-blue-100`}>Overridden</p>
          {status && <p>Reason: {reason}</p>}
        </div>
      )
    }
    if (!expirationDate || expirationDate.length === 0) return '-'
    let todayDate = undefined

    let calculateBasedOn = config?.[itemType]?.calculateBasedOn
    if (_calculateBasedOn !== undefined) calculateBasedOn = _calculateBasedOn

    if (calculateBasedOn == 2) todayDate = DateTime.fromJSDate(new Date(`${closingDate}T12:00:00`))

    const isBefore = isBeforeOrEqualToday(expirationDate, todayDate as any)

    let success = false
    if (seasonType == 1 && !isBefore) success = true
    if (seasonType == 0 && isBefore) {
      if (type === 0) success = validDateMMDDYYYY(date) && new Date(date) < new Date(expirationDate)
      else success = true
    }

    if (success) {
      return <div className="text-[14px] px-2 py-1 text-center bg-green-100 w-[120px]">Passed</div>
    }
    return <div className="text-[14px] px-2 py-1 text-center bg-red-100 w-[120px]">Failed</div>
  }

  return (
    <div className={`LoanChangeRequest-container ${!child && 'px-2 py-6'}`}>
      {!child && <Overview title="Expiration Dates" />}
      <div className="max-w-screen-2xl m-auto">
        <div className={`relative bg-white ${!child && 'shadow1 rounded mb-4 p-4'}`}>
          <LayoutLoading show={loading} />
          <div className="flex flex-wrap justify-between mb-2 mt-2">
            <h1 className="text-2xl font-bold flex items-center">
              <span>Expiration Dates</span>{' '}
            </h1>
            <Button onClick={() => setShowAddModal(true)}>Additional Expiration Dates</Button>
          </div>
          {closingDate && <p className="mb-4">Closing Date: {formatDate(closingDate)}</p>}
          {renderTable(0)}

          <div className="flex flex-wrap justify-between mb-2 mt-6">
            <h1 className="text-xl font-bold flex items-center ml-3">
              <span>- Seasoning Requirements</span>{' '}
            </h1>
          </div>
          {renderTable(1)}
        </div>
      </div>
      {isOpenType == 'status' && (
        <ExpirationDatesStatus item={data[selectedType]} onClose={onCloseModal} onUpdate={onUpdateReason} />
      )}
      {(isOpenType == 'date' || isOpenType == 'expirationDate') && (
        <ExpirationDatesUpdate
          type="date"
          value={data[selectedType].date}
          title={data[selectedType].title}
          subTitle={isOpenType == 'date' ? 'Date' : 'Expiration Date'}
          onClose={onCloseModal}
          onUpdate={onUpdateDate}
        />
      )}
      {isOpenType == 'day' && (
        <ExpirationDatesUpdate
          type="days"
          value={data[selectedType].days}
          title={data[selectedType].title}
          subTitle="Days"
          onClose={onCloseModal}
          onUpdate={onUpdateDays}
        />
      )}
      {isOpenType == 'prop' && (
        <ExpirationDatesPropUpdate
          title={data[selectedType].title}
          data={data[selectedType]}
          onClose={onCloseModal}
          onUpdate={onUpdateProp}
        />
      )}
      {historyType !== '' && <HistoryModal data={data[historyType]} onClose={() => setHistoryType('')} />}
      {showAddModal && <AddExpirationDateTypeModal data={null} onClose={onAddAdditional} />}
      {isOpenType == 'additionalEdit' && (
        <AddExpirationDateTypeModal
          data={{
            title: data[selectedType].title,
            days: data[selectedType].days,
            type: data[selectedType].type,
            seasonType: data[selectedType].seasonType,
            isCoBorrower: data[selectedType].isCoBorrower,
            isCustom: 1,
            calculateBasedOn: data[selectedType].calculateBasedOn,
          }}
          onClose={onUpdateAdditional}
        />
      )}
    </div>
  )
}
